import React, { ReactElement, useEffect, useMemo, useCallback, useState } from "react";
import { ResourceTagListProps, ResourceTagListStateProps } from "./ResourceTagList.types";
import { useSelector, useDispatch } from "react-redux";
import { queryResourceTags } from "src/redux";
import {
    CreateButtonProps,
    DataList,
    DataListColumn,
    DataListFetchHandler,
} from "../DataList";
import { ResourceTagFormDrawer } from "../ResourceTagForm";
import SpintrList from "src/ui/components/SpintrList/SpintrList";

/**
 * Renders the resource tag list component.
 *
 * @param {ResourceTagListProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function ResourceTagList({
}: ResourceTagListProps): ReactElement | null {
    const listRef = React.createRef<SpintrList>();
    const dispatch = useDispatch();
    const {
        tags,
        isLoading,
    } = useSelector<Spintr.AppState, ResourceTagListStateProps>(
        (appState) => ({
            tags: appState.qualityHub.tagsQueryWindow.map(
                (id) => appState.qualityHub.resourceTagsByIds[id],
            ),
            isLoading: appState.qualityHub.loadingResourceTags,
        }),
    );

    const [state, setState] = useState({
        drawerOpen: false,
        selectedTagId: undefined,
    });

    const columns = useMemo<DataListColumn[]>(
        () => [{
            key: "name",
            name: "Name",
            fieldName: "name",
            minWidth: 100,
            maxWidth: 200,
            isSortable: false,
        }],
        []
    );

    const createButton = useMemo<CreateButtonProps>(() => ({
        onClick: () => setState((prevState) => ({
            ...prevState,
            drawerOpen: true,
            selectedTagId: undefined,
        })),
    }), [setState]);

    const onDrawerDismissed = useCallback(
        (_: boolean) => {
            setState((prevState) => ({
                ...prevState,
                drawerOpen: false,
            }));

            dispatch(queryResourceTags(0, 100));
        },
        [dispatch, setState, listRef],
    );

    const listData = useMemo(() => ({
        data: tags,
        totalCount: tags.length,
    }), [tags]);

    const fetch = useCallback<DataListFetchHandler>(
        () => new Promise((resolve) => resolve({
            data: listData.data,
            total: listData.totalCount,
        })),
        [listData]);

    useEffect(() => {
        dispatch(queryResourceTags(0, 100));
    }, []);

    return (
        <div className="ResourceTagList">
            <DataList createButton={createButton}
                      data={listData}
                      fetch={fetch}
                      columns={columns}
                      isLoading={isLoading}
                      orderByColumn={"name"}
                      ref={listRef} />

            <ResourceTagFormDrawer onDismiss={onDrawerDismissed}
                                   open={state.drawerOpen} />
        </div>
    );
}

export default ResourceTagList;

